import { useDispatch, useSelector } from "react-redux";
import { FavouritesHeader } from "../../../../components/FavouritesHeader";
import FilterHeaderButton from "../../../../layouts/Filter/components/FilterHeaderButton";
import styles from "./EditFavouritesHeader.module.scss";
import { isValidName } from "../../../../lib/string";
import { sendMessage } from "../../../../lib/postMessage";
import {
  SELECT_FAVOURITE,
  SET_ACTIVE_VIEW,
  SET_WARNING,
} from "../../../../constants/actions";
import {
  ADD_FAVOURITES_VIEW,
  EDIT_FAVOURITES_VIEW,
  MY_FAVOURITES_VIEW,
  WARNING_VIEW,
} from "../../../../constants";

export const EditFavouritesHeader = () => {
  const dispatch = useDispatch();
  const { id, name, odds, markets } = useSelector((state) => state.favourites);
  const { storedFavourites } = useSelector((state) => state.filter);
  const { previousView } = useSelector((state) => state.view);

  const checkIfFavouriteHasBeenUpdated = () => {
    const favourite = storedFavourites.find(({ id: favId }) => favId === id);
    if (!favourite) return false;
    const { name: favName, odds: favOdds, markets: favMarkets } = favourite;
    const { minOdds: favMinOdds, maxOdds: favMaxOdds } = favOdds || {};
    const { minOdds, maxOdds } = odds || {};

    // if names are different
    const areNamesDifferent = favName !== name;
    // if min odds are different some cases may mean favMinOdds is undefined
    // so we need to check if favMinOdds is undefined and minOdds is not null
    const areMinOddsDifferent =
      favMinOdds !== undefined && minOdds !== null && favMinOdds !== minOdds;
    // if max odds are different some cases may mean favMaxOdds is undefined
    // so we need to check if favMaxOdds is undefined and maxOdds is not null
    const areMaxOddsDifferent =
      (favMaxOdds !== undefined || favMaxOdds !== null) &&
      maxOdds !== null &&
      favMaxOdds !== maxOdds;
    // if markets are different
    const areMarketsDifferent =
      // compare the length of both arrays
      (markets && favMarkets && favMarkets.length !== markets.length) ||
      // check if the user has selected a favourite with markets
      (!favMarkets && markets?.length > 0) ||
      // check if each market selected is present in the favourites markets array
      !markets?.every((market) => favMarkets?.includes(market));

    // if odds are different
    const areOddsDifferent =
      odds &&
      favOdds &&
      favOdds.minOdds !== odds.minOdds &&
      favOdds.maxOdds !== odds.maxOdds;

    return (
      areNamesDifferent ||
      areMinOddsDifferent ||
      areMaxOddsDifferent ||
      areMarketsDifferent ||
      areOddsDifferent
    );
  };
  const saveIsDisabled =
    !name ||
    !isValidName(name) ||
    (odds.minOdds === null && odds.maxOdds === null && markets.length === 0) ||
    !checkIfFavouriteHasBeenUpdated();

  const resetFavouriteValues = () => {
    dispatch({
      type: "RESET_FAVOURITE_OPTIONS",
    });
  };

  const clearAllSelections = () => {
    dispatch({
      type: "CLEAR_ALL_FAVOURITES_SELECTIONS",
    });
  };
  const clearAllIsDisabled =
    odds.minOdds === null && odds.maxOdds === null && markets.length === 0;

  function sendUpdatedFavourite() {
    const updatedFavourite = { id, name, odds, markets };
    const favouriteIndex = storedFavourites.findIndex(
      ({ id: favId }) => favId === id
    );

    if (favouriteIndex === -1) return;

    const favourites = [...storedFavourites];
    favourites[favouriteIndex] = updatedFavourite;
    sendMessage({
      type: "UpdateFavourites",
      payload: favourites,
    });

    dispatch({
      type: "CLEAR_ALL_FAVOURITES_SELECTIONS",
    });

    dispatch({
      type: SET_ACTIVE_VIEW,
      payload: previousView ?? "",
    });
  }

  function showWarning() {
    dispatch({
      type: SET_WARNING,
      payload: "UNSAVED",
    });
    dispatch({
      type: SET_ACTIVE_VIEW,
      payload: WARNING_VIEW,
    });
  }

  function goBackToFavouritesList() {
    const hasUpdated = checkIfFavouriteHasBeenUpdated();
    if (hasUpdated) {
      showWarning();
      return;
    } else {
      const view =
        previousView === "" || previousView === "betShuffle"
          ? MY_FAVOURITES_VIEW
          : previousView;

      clearAllSelections();

      dispatch({
        type: SET_ACTIVE_VIEW,
        payload: view,
      });
    }
  }

  const closeFavourites = () => {
    const hasUpdated = checkIfFavouriteHasBeenUpdated();
    if (hasUpdated) {
      showWarning();
    } else {
      clearAllSelections();
      dispatch({
        type: SET_ACTIVE_VIEW,
        payload:
          previousView === EDIT_FAVOURITES_VIEW ||
          previousView === ADD_FAVOURITES_VIEW
            ? ""
            : previousView,
      });
    }
  };

  const heading = name || "Update Favourite";

  return (
    <>
      <FavouritesHeader
        heading={heading}
        goBack={goBackToFavouritesList}
        close={closeFavourites}
      >
        <div className={styles["filterHeaderContainer--buttons"]}>
          <FilterHeaderButton
            name="Clear all"
            ariaLabel="Clear all selections"
            onClick={resetFavouriteValues}
            disabled={clearAllIsDisabled}
          />
          <FilterHeaderButton
            name="Save"
            ariaLabel="Update favourites"
            onClick={sendUpdatedFavourite}
            disabled={saveIsDisabled}
          />
        </div>
      </FavouritesHeader>
    </>
  );
};
